import { useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

export type PortalLang = 'es' | 'en';

export type PortalQuery = {
  quoteID: string;
  token: string;
  state: string;
  view: string;
  lang: PortalLang;
};

export function readPortalSearch(search: string): PortalQuery {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return {
    quoteID: params.get('quoteID') || params.get('quoteId') || '',
    token: params.get('token') || '',
    state: params.get('state') || '',
    view: params.get('view') || '',
    lang: params.get('lang') === 'en' ? 'en' : 'es',
  };
}

export function buildPortalSearch(
  current: PortalQuery,
  extra: Partial<Pick<PortalQuery, 'quoteID' | 'token' | 'state' | 'view' | 'lang'>> = {},
): string {
  const params = new URLSearchParams();
  const quoteID = extra.quoteID ?? current.quoteID;
  const token = extra.token ?? current.token;
  const lang = extra.lang ?? current.lang;
  const state = extra.state ?? current.state;
  const view = extra.view ?? current.view;
  if (quoteID) params.set('quoteID', quoteID);
  if (token) params.set('token', token);
  if (state) params.set('state', state);
  if (view) params.set('view', view);
  if (lang === 'en') params.set('lang', 'en');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function buildPortalPath(
  pathname: string,
  current: PortalQuery,
  extra: Partial<Pick<PortalQuery, 'quoteID' | 'token' | 'state' | 'view' | 'lang'>> & {
    dropState?: boolean;
    dropView?: boolean;
  } = {},
): string {
  const next: PortalQuery = {
    quoteID: extra.quoteID ?? current.quoteID,
    token: extra.token ?? current.token,
    state: extra.dropState ? '' : (extra.state ?? current.state),
    view: extra.dropView ? '' : (extra.view ?? current.view),
    lang: extra.lang ?? current.lang,
  };
  return `${pathname}${buildPortalSearch(next)}`;
}

export function usePortalQuery(): PortalQuery {
  const { search } = useLocation();
  return useMemo(() => readPortalSearch(search), [search]);
}

export function usePortalNavigation() {
  const query = usePortalQuery();
  const [, setSearchParams] = useSearchParams();

  const path = (
    pathname: string,
    extra: Parameters<typeof buildPortalPath>[2] = {},
  ) => buildPortalPath(pathname, query, extra);

  /* OUW-106: no visible toggle. Kept as an optional ?lang= override for QA/dev. */
  const setLang = (lang: PortalLang) => {
    const next = new URLSearchParams(buildPortalSearch(query, { lang }).replace(/^\?/, ''));
    setSearchParams(next, { replace: true });
  };

  return { query, path, setLang };
}
